import InstallPrompt from "@/components/InstallPrompt";
import { NavMain } from "@/components/nav-main";
import { NavUser } from "@/components/nav-user";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { createFileRoute, Link, Outlet, redirect } from "@tanstack/react-router";
import { Home, SquareTerminal, TriangleAlert } from "lucide-react";

export const Route = createFileRoute("/dashboard/worker")({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    if (!context.auth.loading && !context.auth.profileLoading) {
      if (!context.auth.user || context.auth.userProfile?.type !== "worker") {
        throw redirect({
          to: "/login",
        });
      }
    }
  },
});

function RouteComponent() {
  const { userProfile } = useAuth();

  // Show error if access is revoked or account is deleted
  if (userProfile?.accessRevoked || userProfile?.isDeleted === true) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="max-w-md text-center">
          <h1 className="mb-4 text-4xl font-bold text-gray-900">Access Denied</h1>
          <p className="mb-6 text-lg text-gray-600">
            Your account access has been {userProfile?.isDeleted ? "deactivated" : "revoked"}.
            Please contact your administrator for assistance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Fixed Alert Banner */}
      <Alert variant="warning" className="fixed top-0 z-50 rounded-none border-x-0 border-t-0">
        <TriangleAlert className="size-4" />
        <AlertTitle>Your account currently has a pending balance.</AlertTitle>
        <AlertDescription>
          To ensure uninterrupted access to the CRM, please complete the payment
          by 1st Friday 3pm EST. After this time, system access may be
          temporarily limited until the balance is cleared. If you need any
          assistance, feel free to contact us.
        </AlertDescription>
      </Alert>
      <div className="pt-[76px]">
        <SidebarProvider>
          <AppSidebar />
          <SidebarInset>
            <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 data-[orientation=vertical]:h-4" />
            {/* <Breadcrumb>
                  <BreadcrumbList>
                    <BreadcrumbItem className="hidden md:block">
                      <BreadcrumbLink href="#">
                        Building Your Application
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator className="hidden md:block" />
                    <BreadcrumbItem>
                      <BreadcrumbPage>Data Fetching</BreadcrumbPage>
                    </BreadcrumbItem>
                  </BreadcrumbList>
                </Breadcrumb> */}
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          <Outlet />
          <InstallPrompt />
        </div>
      </SidebarInset>
        </SidebarProvider>
      </div>
    </>
  );
}

function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const data = {
    navMain: [
      {
        title: "Dashboard",
        url: "/dashboard/worker",
        icon: SquareTerminal,
        isActive: true,
        items: [
          {
            title: "Home",
            url: "/dashboard/worker",
            icon: Home,
          },
        ],
      },
    ],
  };
  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader className="flex flex-col items-center justify-center">
        <Link to="/dashboard/worker" className="flex flex-col items-center">
          <img src="/mainLogo.svg" alt="GetHandle" className="py-8" />
          <span className="text-[10px] text-gray-400 leading-none -mt-5 mb-3 tracking-wide">
            v{APP_VERSION}
          </span>
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={data.navMain} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
